import React, { useState, useEffect } from 'react';
import {
  tax39aState, tax39aCheck, tax39aOtp, tax39aApply,
  tax39aBizType, tax39aUpload, tax39aRemoveDoc, tax39aIntent,
} from './auth';
import { Field } from './fields';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

// Conditional step 2 — Άρθρο 39Α (αποφορολόγηση). Only rendered when the cart has
// eligible products (CheckoutNext gates it on cfg.tax39a.available). Two paths:
//   • instant  → AADE ΑΦΜ check + representative identity + one-time code (Su1/Su2/Su3)
//   • manual   → upload identification documents; order is held for staff review.
// Requires a business invoice (τιμολόγιο) with ΑΦΜ from step 1.
export default function Tax39A({ value, cart, onCart, onNext, onBack, onEditBilling }) {
  const init = cfg.tax39a || {};
  const [state, setState] = useState(init);
  const afm = (value.invoice && value.invoice.billing_vat) || value.afm || init.afm || '';
  const isInvoice = value.docType === 'invoice' && !!afm;

  const [mode, setMode] = useState(init.applied ? 'instant' : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // instant — the step only renders once the ΑΦΜ is validated (gated in CheckoutNext),
  // so default to 'validated'; the mount refresh below reconciles with the server.
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(((init.afmStatus && init.afmStatus.status) === 'rejected') ? 'rejected' : 'validated'); // validated|rejected|none
  const [repr, setRepr] = useState({ type: 1, no: '', mobile: '' });
  const [otpId, setOtpId] = useState('');
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpForm, setOtpForm] = useState({ username: '', password: '', reprAfm: '' });
  const [applied, setApplied] = useState(!!init.applied);

  // manual
  const docTypes = init.docTypes || [];
  const [bizType, setBizType] = useState((init.docs && init.docs.bizType) || '');
  const [files, setFiles] = useState((init.docs && init.docs.files) || []);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    tax39aState().then(({ ok, data }) => {
      if (!ok) return;
      setState(data);
      if (data.afmStatus) setStatus(data.afmStatus.status || 'none');
      if (data.docs) { setBizType(data.docs.bizType || ''); setFiles(data.docs.files || []); }
      setApplied(!!data.applied);
      if (data.applied) setMode('instant');
      else if (data.docs && data.docs.bizType) setMode('manual');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function chooseMode(m) {
    setError('');
    setMode(m);
    // Manual and instant are mutually exclusive server-side (biz_type gates the
    // held-order path). Clear the manual flag whenever we leave the manual path.
    if (m !== 'manual') { await tax39aBizType(''); }
    if (m === 'skip') { await tax39aIntent(false); if (onCart) refreshCart(); }
  }

  async function refreshCart() {
    const { ok, data } = await tax39aIntent(applied ? true : false);
    if (ok && data.cart && onCart) onCart(data.cart);
  }

  async function check() {
    setChecking(true); setError('');
    const { ok, data } = await tax39aCheck(afm);
    setChecking(false);
    if (ok) {
      setStatus(data.status);
      if (data.status !== 'validated') setError('Το ΑΦΜ δεν είναι σε κανονικό καθεστώς ΦΠΑ, άρα δεν είναι επιλέξιμο για 39Α.');
    } else { setStatus(data.status || 'error'); setError(data.message || 'Σφάλμα ελέγχου.'); }
  }

  async function genOtp() {
    setBusy(true); setError('');
    const { ok, data } = await tax39aOtp(otpForm.username, otpForm.password, otpForm.reprAfm);
    setBusy(false);
    if (ok) { setOtpId(data.otpId); setOtpOpen(false); }
    else setError(data.message || 'Δεν ήταν δυνατή η δημιουργία κωδικού.');
  }

  async function apply() {
    if (!repr.no || !repr.mobile || !otpId) { setError('Συμπλήρωσε τα στοιχεία αγοραστή και τον Κωδικό μιας χρήσης.'); return; }
    setBusy(true); setError('');
    const { ok, data } = await tax39aApply({ afm, reprType: repr.type, reprNo: repr.no, reprMobile: repr.mobile, otpId });
    setBusy(false);
    if (ok) { setApplied(true); await tax39aBizType(''); if (data.cart && onCart) onCart(data.cart); }
    else setError(data.message || 'Η ΑΑΔΕ δεν ενέκρινε την απαλλαγή.');
  }

  async function pickBiz(k) { setBizType(k); await tax39aBizType(k); }

  async function onUpload(list) {
    if (!list || !list.length) return;
    setUploading(true); setError('');
    for (const f of Array.from(list)) {
      const { ok, data } = await tax39aUpload(f);
      if (ok && data.files) setFiles(data.files);
      else { setError(data.message || 'Αποτυχία μεταφόρτωσης.'); break; }
    }
    setUploading(false);
  }

  async function removeDoc(id) {
    const { ok, data } = await tax39aRemoveDoc(id);
    if (ok && data.files) setFiles(data.files);
  }

  const selBiz = docTypes.find((d) => d.key === bizType);
  const manualReady = !!bizType && files.length > 0;

  return (
    <section className="cn-step1 cn-39a">
      <button type="button" className="cn-steplink" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
        Πίσω
      </button>
      <h3 className="cn-section-title">ΦΠΑ 39Α (αποφορολόγηση)</h3>
      <p className="cn-39a__intro">Τα προϊόντα σου (κινητά / tablets) είναι επιλέξιμα για απαλλαγή ΦΠΑ βάσει Άρθρου 39Α για επιχειρήσεις.</p>

      {!isInvoice ? (
        <div className="cn-39a__need-invoice">
          <p>Η αποφορολόγηση 39Α ισχύει μόνο για <strong>τιμολόγιο επιχείρησης</strong>. Επίλεξε «Τιμολόγιο» και συμπλήρωσε το ΑΦΜ στο 1ο βήμα.</p>
          <div className="cn-step-nav">
            <button type="button" className="cn-btn cn-btn--ghost" onClick={onEditBilling}>Επιλογή τιμολογίου</button>
            <button type="button" className="cn-btn cn-btn--primary" onClick={() => { tax39aIntent(false); onNext(); }}>Συνέχεια χωρίς 39Α</button>
          </div>
        </div>
      ) : applied ? (
        <div className="cn-39a__applied">
          <div className="cn-39a__badge"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg> Η απαλλαγή ΦΠΑ 39Α εφαρμόστηκε (ΑΦΜ {afm}).</div>
          <div className="cn-step-nav"><button type="button" className="cn-btn cn-btn--primary cn-next" onClick={onNext}>Συνέχεια στην πληρωμή</button></div>
        </div>
      ) : (
        <>
          <div className="cn-39a__modes">
            <label className={`cn-39a__mode ${mode === 'instant' ? 'is-sel' : ''}`}>
              <input type="radio" name="cn39a-mode" checked={mode === 'instant'} onChange={() => chooseMode('instant')} />
              <span><strong>Άμεση επικύρωση</strong><em>ΑΦΜ + Κωδικός μιας χρήσης (ΑΑΔΕ), 0% ΦΠΑ αμέσως.</em></span>
            </label>
            <label className={`cn-39a__mode ${mode === 'manual' ? 'is-sel' : ''}`}>
              <input type="radio" name="cn39a-mode" checked={mode === 'manual'} onChange={() => chooseMode('manual')} />
              <span><strong>Ανάρτηση εγγράφων ταυτοποίησης</strong><em>Στέλνεις δικαιολογητικά και η παραγγελία κρατείται μέχρι τον έλεγχο.</em></span>
            </label>
            <label className={`cn-39a__mode ${mode === 'skip' ? 'is-sel' : ''}`}>
              <input type="radio" name="cn39a-mode" checked={mode === 'skip'} onChange={() => chooseMode('skip')} />
              <span><strong>Χωρίς 39Α</strong><em>Συνέχισε κανονικά με ΦΠΑ.</em></span>
            </label>
          </div>

          {mode === 'instant' && (
            <div className="cn-39a__panel">
              <div className="cn-39a__afmrow">
                <span>ΑΦΜ: <strong>{afm}</strong></span>
                {status === 'validated'
                  ? <span className="cn-39a__ok">✓ Επιλέξιμο</span>
                  : <button type="button" className="cn-btn cn-btn--ghost" disabled={checking} onClick={check}>{checking ? 'Έλεγχος…' : 'Έλεγχος επιλεξιμότητας'}</button>}
              </div>

              {status === 'validated' && (
                <div className="cn-39a__form">
                  <div className="cn-grid">
                    <label className="cn-field">
                      <span className="cn-field__label">Τύπος ταυτοποίησης</span>
                      <div className="cn-field__control">
                        <select className="cn-input cn-select" value={repr.type} onChange={(e) => setRepr({ ...repr, type: Number(e.target.value) })}>
                          <option value={1}>Δελτίο Αστυνομικής Ταυτότητας</option>
                          <option value={2}>Διαβατήριο</option>
                          <option value={3}>Άλλο</option>
                        </select>
                      </div>
                    </label>
                    {/* AADE wants the LAST five characters. slice(0, 5) took the
                        first, so a pasted "ΑΟ042719" went up as 04271 — and a
                        maxLength would truncate the paste at the wrong end before
                        this ever ran, so there deliberately isn't one. */}
                    <Field label="5 τελευταία ψηφία εγγράφου" value={repr.no} inputMode="numeric" onChange={(v) => setRepr({ ...repr, no: v.replace(/\D/g, '').slice(-5) })} />
                  </div>
                  <Field label="Κινητό αγοραστή" value={repr.mobile} type="tel" inputMode="numeric" full onChange={(v) => setRepr({ ...repr, mobile: v })} />
                  <Field label="Κωδικός μιας χρήσης (ΑΑΔΕ)" value={otpId} inputMode="numeric" placeholder="π.χ. 12345678" full onChange={(v) => setOtpId(v.replace(/\D/g, ''))} />
                  <button type="button" className="cn-linkbtn" onClick={() => setOtpOpen((v) => !v)}>{otpOpen ? 'Απόκρυψη' : 'Δεν έχω κωδικό, δημιουργία με τους Ειδικούς Κωδικούς μου'}</button>
                  {otpOpen && (
                    <div className="cn-39a__otpgen">
                      <p className="cn-39a__hint">Χρησιμοποιούμε τους «Ειδικούς Κωδικούς» σου (ΑΑΔΕ) μόνο για τη δημιουργία του κωδικού και δεν αποθηκεύονται.{init.guideUrl && <> Δεν τους έχεις; <a href={init.guideUrl} target="_blank" rel="noopener noreferrer">Δες τον οδηγό.</a></>}</p>
                      <div className="cn-grid">
                        <Field label="Username ΑΑΔΕ" value={otpForm.username} onChange={(v) => setOtpForm({ ...otpForm, username: v })} />
                        <Field label="Password ΑΑΔΕ" type="password" value={otpForm.password} onChange={(v) => setOtpForm({ ...otpForm, password: v })} />
                      </div>
                      <button type="button" className="cn-btn cn-btn--ghost" disabled={busy} onClick={genOtp}>{busy ? 'Δημιουργία…' : 'Δημιουργία κωδικού'}</button>
                    </div>
                  )}
                </div>
              )}

              {error && <div className="cn-errors" role="alert"><ul><li>{error}</li></ul></div>}
              <div className="cn-step-nav">
                <button type="button" className="cn-btn cn-btn--primary cn-next" disabled={busy || status !== 'validated'} onClick={apply}>{busy ? 'Εφαρμογή…' : 'Εφαρμογή απαλλαγής 39Α'}</button>
              </div>
            </div>
          )}

          {mode === 'manual' && (
            <div className="cn-39a__panel">
              <p className="cn-39a__hint">Ανάλογα με τη μορφή της επιχείρησής σου, ανέβασε τα εξής έγγραφα ταυτοποίησης:</p>
              <div className="cn-39a__biz">
                {docTypes.map((d) => (
                  <label key={d.key} className={`cn-39a__bizopt ${bizType === d.key ? 'is-sel' : ''}`}>
                    <input type="radio" name="cn39a-biz" checked={bizType === d.key} onChange={() => pickBiz(d.key)} />
                    <span><strong>{d.label}</strong><em>{d.docs.join(', ')}</em></span>
                  </label>
                ))}
              </div>

              {bizType && (
                <div className="cn-39a__upload">
                  <label className="cn-39a__drop">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => onUpload(e.target.files)} />
                    <span>{uploading ? 'Μεταφόρτωση…' : 'Επίλεξε αρχεία (PDF, JPG, PNG, έως 8MB)'}</span>
                  </label>
                  {files.length > 0 && (
                    <ul className="cn-39a__files">
                      {files.map((f) => (
                        <li key={f.id}><span className="cn-39a__fname">{f.name}</span><button type="button" className="cn-linkbtn" onClick={() => removeDoc(f.id)}>Αφαίρεση</button></li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {error && <div className="cn-errors" role="alert"><ul><li>{error}</li></ul></div>}
              <p className="cn-39a__note">Η παραγγελία θα καταχωρηθεί σε αναμονή. Μετά τον έλεγχο των εγγράφων εφαρμόζουμε την απαλλαγή και σου ζητάμε την πληρωμή του τελικού ποσού.</p>
              <div className="cn-step-nav">
                <button type="button" className="cn-btn cn-btn--primary cn-next" disabled={!manualReady} onClick={onNext}>Συνέχεια</button>
              </div>
            </div>
          )}

          {mode === 'skip' && (
            <div className="cn-step-nav"><button type="button" className="cn-btn cn-btn--primary cn-next" onClick={onNext}>Συνέχεια στην πληρωμή</button></div>
          )}
        </>
      )}
    </section>
  );
}
